import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { getSubjects } from '../api/getSubjects';
import { addSubject } from '../api/addSubject';
import { deleteSubject } from '../api/deleteSubject';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const CoursesLayer = () => {
  const [subjects, setSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newSubject, setNewSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const subjectsPerPage = 10;

  const fetchSubjects = async () => {
    const token = localStorage.getItem("token");
    const data = await getSubjects(token);
    setSubjects(data);
    setInitialLoading(false);
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const filteredSubjects = subjects.filter(subject =>
    subject.subjectname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastSubject = currentPage * subjectsPerPage;
  const indexOfFirstSubject = indexOfLastSubject - subjectsPerPage;
  const currentSubjects = filteredSubjects.slice(indexOfFirstSubject, indexOfLastSubject);
  const totalPages = Math.ceil(filteredSubjects.length / subjectsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;

    setLoading(true);
    const result = await addSubject(newSubject.trim());

    if (result.success) {
      Swal.fire("✅", "Subject added successfully!", "success");
      setNewSubject('');
      fetchSubjects();
    } else {
      Swal.fire("❌", "Failed to add subject: " + result.message, "error");
    }

    setLoading(false);
  };

  const handleDelete = async (subjectname) => {
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You are about to delete "${subjectname}". This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      const res = await deleteSubject(subjectname);
      if (res.success) {
        Swal.fire("Deleted!", `"${subjectname}" has been deleted.`, "success");
        fetchSubjects();
      } else {
        Swal.fire("Failed!", "Something went wrong while deleting.", "error");
      }
    }
  };

  const exportToExcel = () => {
    const data = filteredSubjects.map((subj, i) => ({
      "S.L": i + 1,
      "Name": subj.subjectname
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Courses");
    XLSX.writeFile(workbook, "courses.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Subjects List", 14, 20);
    const tableColumn = ["S.L", "Name"];
    const tableRows = filteredSubjects.map((subj, i) => [
      i + 1,
      subj.subjectname
    ]);
    autoTable(doc, {
      startY: 25,
      head: [tableColumn],
      body: tableRows,
    });
    doc.save("courses.pdf");
  };

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '6px solid #e0e0e0',
          borderTop: '6px solid #45B369',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button onClick={exportToExcel} className="btn btn-success btn-sm">Excel Export</button>
            <button onClick={exportToPDF} className="btn btn-danger btn-sm">PDF Export</button>
          </div>
          <button
            type="button"
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#addCourseModal"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Courses
          </button>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.L</th>
                  <th className="text-center">Name</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentSubjects.map((subj, index) => (
                  <tr key={subj.id}>
                    <td>{indexOfFirstSubject + index + 1}</td>
                    <td className="text-center">{subj.subjectname}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          type="button"
                          className="remove-item-button bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleDelete(subj.subjectname)}
                        >
                          <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {indexOfFirstSubject + 1} to {Math.min(indexOfLastSubject, filteredSubjects.length)} of {filteredSubjects.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                  <button onClick={() => handlePageChange(i + 1)} className="page-link">{i + 1}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Add Subject Modal */}
      <div className="modal fade" id="addCourseModal" tabIndex={-1} aria-labelledby="addCourseModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border-bottom">
              <h5 className="modal-title" id="addCourseModalLabel">Add New Subject</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body p-24">
              <form onSubmit={handleAddSubject}>
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label text-sm mb-8">Subject Name</label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="Enter Subject Name"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button type="reset" className="btn btn-outline-danger px-50 py-11 radius-8">Reset</button>
                    <button type="submit" className="btn btn-primary px-50 py-12 radius-8" disabled={loading} data-bs-dismiss="modal">
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoursesLayer;
